import CresAxios from "../../util/CresAxios";

import {Column, Filter, ReportQuery, Sort} from "./querySpec";

/**
 * The two calls a report builder makes. THE ONLY PLACE THE ENDPOINT IS NAMED.
 *
 * NO SQL CROSSES EITHER WAY. The browser posts a specification and the server compiles it; the
 * datasets it may name come from the server and are built for this caller from the object types in
 * their realm. A dataset belonging to another tenant is not refused here -- it is not in the list,
 * which is a quieter answer than a refusal and a stronger one.
 *
 * NEITHER CALL SENDS A REALM. It is decided from the session, server side, and a specification that
 * so much as mentions one is refused -- see SERVER_INJECTED in querySpec, and `_refuse_injected_keys`
 * in cres_t5/service/report_query.py, which is the half that enforces it.
 */

const URL = "t10/v1/reports";

export type DatasetField = {
    Field: string;
    Label: string;
    Role: "dimension" | "measure";
    Retired?: boolean;
    /** Where the column came from: "record" is a column every record of every type has, "form" is
     *  an answer somebody's form design asks for. Optional, because a response from a server that
     *  predates the marker has none -- and everything that predates it is a form field. */
    Origin?: "record" | "form";
};

export type Dataset = {Name: string; Fields: DatasetField[]};

/** Something a person should know about a dataset, that nothing refuses over. */
export type Note = {
    kind: string;
    dataset?: string;
    code?: string;
    /** The field's own name, as the field list shows it. The CODE is what a saved report stores;
     *  it is not what anybody reading this is looking at. */
    label?: string;
    detail: string;
};

export type ResultColumn = {Key: string; Label: string; Role?: string; Field?: string};

export type ReportResult = {
    Columns: ResultColumn[];
    Rows: unknown[][];
    RowCount: number;
    /** The result reached the cap. Said rather than inferred: a truncated report looks exactly like
     *  a complete one, and somebody quotes the number. */
    Truncated: boolean;
};

export function getReportDatasets(): Promise<{Datasets: Dataset[]; Notes: Note[]}> {
    return CresAxios({method: "GET", url: URL})
        .then((response: any) => ({
            Datasets: response.data?.Datasets ?? [],
            Notes: response.data?.Notes ?? [],
        }));
}

/**
 * Run one.
 *
 * POST, and not because running a report changes anything -- it does not. A specification is a
 * structured document with filters and values, and a GET would put all of it in a query string:
 * length-capped, awkward to encode, and logged by every hop in between. A report's filter values
 * are tenant data and have no business in an access log.
 */
export function runReport(query: ReportQuery): Promise<ReportResult> {
    // NO `json: true`. The .js components around this app pass it and get away with it because
    // nothing type-checks them; it is not an axios option and never was. In a .ts file it is a
    // compile error, which is the only reason anybody finds out.
    return CresAxios({method: "POST", url: URL, data: {Query: query}})
        .then((response: any) => ({
            Columns: response.data?.Columns ?? [],
            Rows: response.data?.Rows ?? [],
            RowCount: response.data?.RowCount ?? 0,
            Truncated: !!response.data?.Truncated,
        }));
}

export type {Column, Filter, ReportQuery, Sort};

export type Explained = {
    /** The statement the server would run, with values still bound as %s. */
    Statement: string;
    /** How many values bind. The values themselves are tenant data and never cross. */
    BoundValues: number;
};

/**
 * What this report compiles to, without running it.
 *
 * THE SERVER'S OWN COMPILER ANSWERS THIS, and it has to be. Building a representative statement
 * here from the shelves would be a second implementation of it, and a displayed statement that
 * differs from the executed one is worse than none -- it is believed, and reasoned from.
 */
export function explainReport(query: object): Promise<Explained> {
    return CresAxios({method: "POST", url: URL, data: {Query: query, Explain: true}})
        .then((response: any) => ({
            Statement: response.data?.Statement ?? "",
            BoundValues: response.data?.BoundValues ?? 0,
        }));
}
